import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Lightbulb, Plus, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

const GRADE = { A: "bg-emerald-600", B: "bg-sky-600", C: "bg-amber-500", D: "bg-rose-600" };

export default function PlanSuggestions({ repId, onPlan, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const load = () => api.get("/plans/suggestions", { params: { ...(repId ? { rep_id: repId } : {}), limit: 6 } }).then(({ data }) => setData(data)).catch(() => setData({ suggestions: [] }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [repId, refreshKey]);
  if (!data) return null;
  const list = data.suggestions || [];
  return (
    <Card className="border-0 shadow-md overflow-hidden bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white" data-testid="plan-suggestions">
      <div className="p-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-400 text-slate-900 flex items-center justify-center"><Lightbulb className="w-4 h-4" /></div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/70">Smart suggestions</div>
            <div className="font-heading font-bold">Who to plan next {list.length > 0 && <span className="text-white/60 font-normal">· {list.length}</span>}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={load} className="p-1.5 rounded hover:bg-white/10" data-testid="suggestions-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
          <button onClick={() => setOpen(o => !o)} className="p-1.5 rounded hover:bg-white/10" data-testid="suggestions-toggle">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4">
          {list.length === 0 && <div className="text-sm text-white/70" data-testid="suggestions-empty">Everything that needs attention is already planned. Nice.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {list.map(s => (
              <div key={s.facility_id} className="rounded-lg bg-white/10 backdrop-blur border border-white/10 p-3 flex items-start justify-between gap-2 hover:bg-white/15 transition-colors" data-testid={`suggestion-${s.facility_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><Link to={`/facilities/${s.facility_id}`} className="font-semibold truncate hover:underline">{s.name}</Link>{s.grade && <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono-plex font-bold ${GRADE[s.grade]}`}>{s.health}·{s.grade}</span>}</div>
                  <div className="text-[11px] text-white/70 truncate">{s.city} · {s.referral_status?.replace("_", " ")}</div>
                  <div className="mt-1 flex flex-wrap gap-1">{s.reasons.map(r => <span key={r} className="px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-200 text-[10px]">{r}</span>)}</div>
                </div>
                <Button size="sm" onClick={() => onPlan(s)} className="h-7 text-xs bg-white text-slate-900 hover:bg-indigo-50 shrink-0" data-testid={`suggestion-plan-${s.facility_id}`}><Plus className="w-3 h-3 mr-1" /> Plan</Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
